/** Tab 5: Salary */
import { useState } from 'react'
import {
  Button, Card, Form, message, Space, DatePicker, Select, InputNumber, Row, Col,
  Upload, Tag, Alert, Input,
} from 'antd'
import { UploadOutlined, CheckCircleTwoTone, FileTextOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { WAGE_CATEGORIES } from '@/shared/types/hr'

interface Props { detail: JoiningDetail }

export default function SalaryTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  // Sprint 0151 — `detail.employee` is typed as Record<string,unknown>
  // so we pull the two proof fields off explicitly into typed locals
  // for the upload-status display below.
  const empBag = detail.employee as Record<string, unknown>
  const proofPath = (empBag.last_salary_proof_path as string | null) ?? null
  const proofType = (empBag.last_salary_proof_type as string | null) ?? null

  const initial: Record<string, unknown> = { ...detail.employee }
  if (initial.payroll_effective_from) {
    initial.payroll_effective_from = dayjs(initial.payroll_effective_from as string)
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="Salary Structure" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="salary_type" label="Salary Type">
              <Select
                options={[
                  { value: 'MONTHLY', label: 'Monthly' },
                  { value: 'DAILY', label: 'Daily' },
                  { value: 'PIECE_RATE', label: 'Piece Rate' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="wage_category"
              label="Wage Category"
              tooltip="Minimum Wages Act category — drives the legal minimum wage rate this worker is entitled to. Different from Skill Class (operational) and Category (employment type)."
            >
              <Select
                allowClear
                placeholder="Pick wage board category"
                options={WAGE_CATEGORIES.map((c) => ({
                  label: c.replace(/_/g, ' '), value: c,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="payment_mode" label="Payment Mode">
              <Select
                options={[
                  { value: 'BANK', label: 'Bank' },
                  { value: 'CASH', label: 'Cash' },
                  { value: 'CHEQUE', label: 'Cheque' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="basic_salary" label="Basic Salary">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="hra_amount" label="HRA">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="conveyance_allowance" label="Conveyance">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="medical_allowance" label="Medical">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="special_allowance" label="Special Allowance">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="da_amount" label="DA">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="daily_rate" label="Daily Rate">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="hourly_rate" label="Hourly Rate">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="payroll_effective_from" label="Payroll Effective From">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* ─── Sprint 0151 — Previous Employer + Last Salary ───────────
          MANDATORY before joining is marked complete. Previous-employer +
          last-drawn-salary + proof file must all be populated. The Biometric
          ID is no longer generated here — it (and the employee code) are
          assigned automatically at CHRO final approval. */}
      <Card
        size="small"
        title={
          <Space>
            <FileTextOutlined />
            Previous Employer &amp; Last Drawn Salary
            <Tag color="red">Mandatory</Tag>
          </Space>
        }
        style={{ marginBottom: 12 }}
      >
        <Alert
          type="info" showIcon
          message="Last drawn salary + proof file (salary slip OR bank statement) are required before joining is closed."
          description="The Biometric ID and the employee code are assigned automatically by the system when CHRO gives final approval — not before. The biometric number is unique across all employees and will never clash with any existing one."
          style={{ marginBottom: 12 }}
        />
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="previous_employer_name"
              label="Previous Employer Name"
              rules={[{ required: true, message: 'Previous employer is required' }]}
            >
              <Input placeholder="e.g. ABC Spinning Mills Pvt. Ltd." maxLength={200} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="last_drawn_salary"
              label="Last Drawn Salary (₹/month)"
              rules={[{ required: true, message: 'Last drawn salary is required' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="e.g. 18500.00"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Proof of Last Salary"
              required
              tooltip="Upload the most recent salary slip OR a bank statement showing the salary credit. PDF / JPG / PNG. Max 10 MB."
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {proofPath ? (
                  <Space>
                    <CheckCircleTwoTone twoToneColor="#52c41a" />
                    <span>Uploaded:</span>
                    <Tag color="blue">
                      {proofType === 'SALARY_SLIP' ? 'Salary Slip' : 'Bank Statement'}
                    </Tag>
                    <a
                      href={`/static/${proofPath}`}
                      target="_blank"
                      rel="noreferrer"
                    >View</a>
                  </Space>
                ) : (
                  <Tag color="orange">No proof uploaded yet</Tag>
                )}
                <Upload
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  showUploadList={false}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    try {
                      // Ask the user which type of proof they're uploading
                      const proofType = window.prompt(
                        'Is this a SALARY_SLIP or BANK_STATEMENT?\n\nType "1" for Salary Slip\nType "2" for Bank Statement',
                        '1',
                      )
                      if (!proofType) {
                        onError?.(new Error('cancelled'))
                        return
                      }
                      const mapped = proofType.trim() === '2' ? 'BANK_STATEMENT' : 'SALARY_SLIP'
                      const fd = new FormData()
                      fd.append('file', file as Blob)
                      fd.append('proof_type', mapped)
                      const resp = await apiClient.post(
                        `/joining/${detail.id}/last-salary-proof`,
                        fd,
                        { headers: { 'Content-Type': 'multipart/form-data' } },
                      )
                      onSuccess?.(resp.data)
                      message.success(`${mapped.replace('_', ' ')} uploaded`)
                      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
                    } catch (e) {
                      const err = e as { response?: { data?: { detail?: string } } }
                      message.error(err?.response?.data?.detail ?? 'Upload failed')
                      onError?.(e as Error)
                    }
                  }}
                >
                  <Button icon={<UploadOutlined />}>
                    {proofPath ? 'Replace Proof' : 'Upload Proof'}
                  </Button>
                </Upload>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
